import React, { useMemo, useState, useEffect, useCallback } from "react";
import { SwimlaneCard } from "../SwimlaneCard";
import { useLocalStorage } from "../../hooks/useLocalStorage";

export const SwimlaneView = ({
	pendingReviewMRs,
	myOpenMRs,
	recentlyClosedMRs,
	loading,
	error,
	showMergedColumn = true,
	showDeletedColumn = false,
	hasIncompleteResults = false,
	hideCreatedDaysThreshold = 30,
	hideUpdatedDaysThreshold = 14,
	enableCommentsExpansion = false,
	searchQuery = "",
	onOpenDiff,
	getMRTags = () => [],
	allMRTags = [],
	onAddMRTag,
	onRemoveMRTag,
}) => {
	const [draggedMR, setDraggedMR] = useState(null);
	const [hoverPosition, setHoverPosition] = useState(null);
	const [laneAssignments, setLaneAssignments] = useLocalStorage("swimlaneLaneAssignments", {});

	// Helper function to check if drag and drop is allowed for a lane
	const allowLaneDragAndDrop = (laneId) => {
		return laneId !== "merged";
	};

	// Helper function to get lane from assignment
	const getAssignmentLane = (assignment) => {
		return typeof assignment === 'object' ? assignment.lane : assignment;
	};

	// Helper function to get position from assignment
	const getAssignmentPosition = (assignment) => {
		return typeof assignment === 'object' ? assignment.position : 0;
	};

	// Helper function to get current lane of dragged item
	const getDraggedItemLane = () => {
		if (!draggedMR) return null;
		const currentLaneAssignment = laneAssignments[draggedMR.id];
		return getAssignmentLane(currentLaneAssignment);
	};

	// Helper function to get all items in a lane sorted by position
	const getLaneItems = (laneId) => {
		return Object.entries(laneAssignments)
			.filter(([_, assignment]) => getAssignmentLane(assignment) === laneId)
			.map(([id, assignment]) => ({
				id,
				position: getAssignmentPosition(assignment)
			}))
			.sort((a, b) => a.position - b.position);
	};

	// Helper function to get max position in a lane from assignments object
	const getMaxPositionInLane = useCallback((assignments, laneId) => {
		return Object.entries(assignments)
			.filter(([_, assignment]) => getAssignmentLane(assignment) === laneId)
			.reduce((max, [_, assignment]) => {
				const pos = getAssignmentPosition(assignment);
				return Math.max(max, pos);
			}, -1);
	}, []);

	// Helper function to check if dropping would result in movement
	const checkWouldMove = (laneId, dropPos) => {
		if (!draggedMR) return false;
		
		const laneItems = getLaneItems(laneId);
		const draggedItemIndex = laneItems.findIndex(item => item.id === draggedMR.id);
		
		// If not in same lane, would move
		if (draggedItemIndex === -1) return true;
		
		// If in same lane, check if position would change
		if (dropPos === draggedItemIndex || dropPos === draggedItemIndex + 1) {
			return false;
		}
		
		return true;
	};

	// Helper function to handle cross-lane drag over
	const handleCrossLaneDragOver = (e, targetLaneId) => {
		e.preventDefault();
		if (!draggedMR) return;
		
		const currentLane = getDraggedItemLane();
		
		// If dragging from different lane, show indicator at end
		if (currentLane !== targetLaneId) {
			setHoverPosition({ lane: targetLaneId, position: swimlanes.find(l => l.id === targetLaneId)?.mrs.length || 0 });
		}
	};

	// Helper function to handle cross-lane drop
	const handleCrossLaneDrop = (e, targetLaneId) => {
		e.preventDefault();
		if (!draggedMR) return;
		
		const currentLane = getDraggedItemLane();
		
		// Only handle drop if coming from different lane (cross-column drag)
		if (currentLane !== targetLaneId) {
			const maxPosition = getMaxPositionInLane(laneAssignments, targetLaneId);
			handleDrop(e, targetLaneId, maxPosition + 1);
		}
	};

	// Cleanup and initialization: Hide old/stale MRs and initialize positions for new MRs
	useEffect(() => {
		const allMRs = [
			...pendingReviewMRs,
			...myOpenMRs,
			...recentlyClosedMRs,
		];
		const allMRIds = new Set(allMRs.map(mr => mr.id));

		// Guard: Don't run cleanup if we have no MR data (loading state or API failure)
		if (allMRIds.size === 0) {
			return;
		}

		// Guard: Don't run cleanup if results are incomplete (partial GraphQL response)
		// This prevents MRs from being removed and then re-initialized when they reappear
		if (hasIncompleteResults) {
			return;
		}

		setLaneAssignments(prevAssignments => {
			const currentAssignments = { ...prevAssignments };
			let hasChanges = false;

			// Initialize positions for new MRs that don't have assignments yet
			const initializePositions = (mrs, defaultLane) => {
				mrs.forEach((mr, index) => {
					if (!currentAssignments[mr.id]) {
						const maxPosition = getMaxPositionInLane(currentAssignments, defaultLane);
						
						currentAssignments[mr.id] = {
							lane: defaultLane,
							position: maxPosition + 1,
							createdAt: mr.createdAt,
							updatedAt: mr.updatedAt
						};
						hasChanges = true;
					} else {
						// Backfill missing metadata for existing assignments
						if (!currentAssignments[mr.id].createdAt && mr.createdAt) {
							currentAssignments[mr.id].createdAt = mr.createdAt;
							hasChanges = true;
						}
						if (!currentAssignments[mr.id].updatedAt && mr.updatedAt) {
							currentAssignments[mr.id].updatedAt = mr.updatedAt;
							hasChanges = true;
						}
						// Always update updatedAt to track latest activity
						if (mr.updatedAt && currentAssignments[mr.id].updatedAt !== mr.updatedAt) {
							currentAssignments[mr.id].updatedAt = mr.updatedAt;
							hasChanges = true;
						}
					}
				});
			};

			// Initialize positions for all MRs in their default lanes
			initializePositions(pendingReviewMRs, 'new');
			
			// Initialize myOpenMRs - merged ones go to merged lane, others to new lane
			const mergedMRs = myOpenMRs.filter(mr => mr.state === 'merged');
			const openMRs = myOpenMRs.filter(mr => mr.state !== 'merged');
			initializePositions(mergedMRs, 'merged');
			initializePositions(openMRs, 'new');
			
			// Initialize recentlyClosedMRs - only merged ones are shown
			const closedMergedMRs = recentlyClosedMRs.filter(mr => mr.state === 'merged');
			initializePositions(closedMergedMRs, 'merged');

			// Remove assignments if createdAt and updatedAt exceed configured thresholds
			// Mark them as 'hidden' instead of deleting so they don't reappear in default lanes
			// Safeguard: if updated threshold is higher than created, cap it at created threshold
			const effectiveUpdatedThreshold = hideUpdatedDaysThreshold > hideCreatedDaysThreshold 
				? hideCreatedDaysThreshold
				: hideUpdatedDaysThreshold;
			
			const createdThreshold = new Date();
			createdThreshold.setDate(createdThreshold.getDate() - hideCreatedDaysThreshold);
			const updatedThreshold = new Date();
			updatedThreshold.setDate(updatedThreshold.getDate() - effectiveUpdatedThreshold);
			
			Object.keys(currentAssignments).forEach(mrId => {
				const assignment = currentAssignments[mrId];
				const createdAt = assignment.createdAt;
				const updatedAt = assignment.updatedAt;
				
				if (createdAt && updatedAt && 
						new Date(createdAt) < createdThreshold && 
						new Date(updatedAt) < updatedThreshold &&
						assignment.lane !== 'hidden') {
					currentAssignments[mrId] = {
						...assignment,
						lane: 'hidden',
						hiddenAt: new Date().toISOString()
					};
					hasChanges = true;
				}
			});
			
			// Backfill hiddenAt for items that were hidden before this feature was added
			// AND re-evaluate hidden items against current thresholds (unhide if they no longer meet criteria)
			Object.keys(currentAssignments).forEach(mrId => {
				const assignment = currentAssignments[mrId];
				
				if (assignment.lane === 'hidden') {
					// Backfill hiddenAt if missing
					if (!assignment.hiddenAt) {
						currentAssignments[mrId] = {
							...assignment,
							hiddenAt: new Date().toISOString()
						};
						hasChanges = true;
					}
					
					// Re-evaluate: unhide if no longer meets threshold criteria
					const createdAt = assignment.createdAt;
					const updatedAt = assignment.updatedAt;
					
					if (createdAt && updatedAt) {
						const shouldStillBeHidden = 
							new Date(createdAt) < createdThreshold && 
							new Date(updatedAt) < updatedThreshold;
						
						if (!shouldStillBeHidden) {
							// Find the MR in the data to restore it to its default lane
							const allMRs = [...pendingReviewMRs, ...myOpenMRs, ...recentlyClosedMRs];
							const mr = allMRs.find(m => m.id === mrId);
							
							if (mr) {
								// Determine default lane based on MR state
								let defaultLane = 'new';
								if (mr.state === 'merged') {
									defaultLane = 'merged';
								}
								
								const maxPosition = getMaxPositionInLane(currentAssignments, defaultLane);
								currentAssignments[mrId] = {
									lane: defaultLane,
									position: maxPosition + 1,
									createdAt: mr.createdAt,
									updatedAt: mr.updatedAt
								};
								hasChanges = true;
							}
						}
					}
				}
			});
			
			// Permanently delete assignments that have been hidden for more than 1 month
			const oneMonthAgoForHidden = new Date();
			oneMonthAgoForHidden.setMonth(oneMonthAgoForHidden.getMonth() - 1);
			
			Object.keys(currentAssignments).forEach(mrId => {
				const assignment = currentAssignments[mrId];
				
				if (assignment.lane === 'hidden' && assignment.hiddenAt) {
					const hiddenDate = new Date(assignment.hiddenAt);
					if (hiddenDate < oneMonthAgoForHidden) {
						delete currentAssignments[mrId];
						hasChanges = true;
					}
				}
			});

			return hasChanges ? currentAssignments : prevAssignments;
		});
	}, [pendingReviewMRs, myOpenMRs, recentlyClosedMRs, setLaneAssignments, getMaxPositionInLane, hasIncompleteResults, hideCreatedDaysThreshold, hideUpdatedDaysThreshold]);

	const handleDragStart = (mr) => {
		setDraggedMR(mr);
	};

	const handleDragEnd = () => {
		setDraggedMR(null);
		setHoverPosition(null);
	};

	const handleDeleteItem = (mrId) => {
		setLaneAssignments(prev => {
			const newAssignments = { ...prev };
			const maxPosition = getMaxPositionInLane(prev, 'deleted');
			const existingAssignment = prev[mrId] || {};
			
			newAssignments[mrId] = {
				lane: 'deleted',
				position: maxPosition + 1,
				createdAt: existingAssignment.createdAt,
				updatedAt: existingAssignment.updatedAt
			};
			
			return newAssignments;
		});
	};

	const handleRestoreItem = (mrId) => {
		setLaneAssignments(prev => {
			const newAssignments = { ...prev };
			const maxPosition = getMaxPositionInLane(prev, 'new');
			const existingAssignment = prev[mrId] || {};
			
			newAssignments[mrId] = {
				lane: 'new',
				position: maxPosition + 1,
				createdAt: existingAssignment.createdAt,
				updatedAt: existingAssignment.updatedAt
			};
			
			return newAssignments;
		});
	};

	const handleDrop = (e, targetLaneId, dropPosition) => {
		e.preventDefault();
		if (!draggedMR) return;

			setLaneAssignments(prev => {
			const newAssignments = { ...prev };
			const laneItems = getLaneItems(targetLaneId);
			const draggedItemIndex = laneItems.findIndex(item => item.id === draggedMR.id);
			
			// If dragging within same lane, check if drop would result in no movement
			if (draggedItemIndex !== -1) {
				if (dropPosition === draggedItemIndex || dropPosition === draggedItemIndex + 1) {
					return newAssignments;
				}
			}
			
			// Remove dragged item if it's already in this lane
			const filteredItems = laneItems.filter(item => item.id !== draggedMR.id);
			
			// Adjust drop position if dragging down within same lane
			let adjustedDropPosition = dropPosition;
			if (draggedItemIndex !== -1 && dropPosition > draggedItemIndex) {
				adjustedDropPosition = dropPosition - 1;
			}
			
			// Insert at adjusted drop position
			filteredItems.splice(adjustedDropPosition, 0, { id: draggedMR.id });
			
			// Reassign positions sequentially (0, 1, 2, 3...)
			// This renormalizes positions, eliminating any gaps that may have formed
			// when MRs were removed from the lane. Gaps don't cause functional issues
			// since getLaneItems() sorts by position, but this keeps numbering clean.
			filteredItems.forEach((item, index) => {
				const existingAssignment = prev[item.id] || {};
				newAssignments[item.id] = {
					lane: targetLaneId,
					position: index,
					createdAt: existingAssignment.createdAt || draggedMR.createdAt,
					updatedAt: existingAssignment.updatedAt || draggedMR.updatedAt
				};
			});
			
			return newAssignments;
		});

		setDraggedMR(null);
		setHoverPosition(null);
	};

	const categorizeMRs = useMemo(() => {
		const newMRs = [];
		const toReviewMRs = [];
		const waitingOnAuthorMRs = [];
		const reviewedMRs = [];
		const trackingMRs = [];
		const mergedMRs = [];
		const deletedMRs = [];

		pendingReviewMRs.forEach((mr) => {
			newMRs.push(mr);
		});

		myOpenMRs.forEach((mr) => {
			if (mr.state === "merged") {
				mergedMRs.push(mr);
			} else {
				newMRs.push(mr);
			}
		});

		recentlyClosedMRs.forEach((mr) => {
			if (mr.state === "merged") {
				mergedMRs.push(mr);
			}
		});

		const defaultLanes = {
			new: newMRs,
			toReview: toReviewMRs,
			waitingOnAuthor: waitingOnAuthorMRs,
			reviewed: reviewedMRs,
			tracking: trackingMRs,
			merged: mergedMRs,
			deleted: deletedMRs,
		};

		// Separate MRs into assigned and unassigned
		const assignedMRIds = new Set(Object.keys(laneAssignments));
		const assignedMRs = {};
		const unassignedMRs = {
			new: [],
			toReview: [],
			waitingOnAuthor: [],
			reviewed: [],
			tracking: [],
			merged: [],
			deleted: [],
		};

		// Collect unassigned MRs from default lanes
		Object.entries(defaultLanes).forEach(([laneKey, mrs]) => {
			mrs.forEach(mr => {
				if (!assignedMRIds.has(mr.id)) {
					unassignedMRs[laneKey].push(mr);
				}
			});
		});

		// Process assigned MRs
		Object.entries(laneAssignments).forEach(([mrId, assignment]) => {
			let targetLane = getAssignmentLane(assignment);
			const position = getAssignmentPosition(assignment);
			
			// Skip hidden MRs
			if (targetLane === 'hidden') {
				return;
			}
			
			// Find the MR in the original data
			const allMRs = [...pendingReviewMRs, ...myOpenMRs, ...recentlyClosedMRs];
			const mr = allMRs.find(m => m.id === mrId);
			
			// Override lane assignment if MR state is merged - always show in merged column
			if (mr && mr.state === 'merged') {
				targetLane = 'merged';
			}

			if (targetLane === "new" && mr && mr.state === 'closed') {
				targetLane = 'deleted';
			}
			
			// Track assigned MRs with their positions
			if (mr) {
				if (!assignedMRs[targetLane]) {
					assignedMRs[targetLane] = [];
				}
				assignedMRs[targetLane].push({ mr, position });
			}
		});

		// Build final lanes: positioned MRs first, then unassigned MRs
		const lanes = {};
		Object.keys(defaultLanes).forEach(laneKey => {
			const positioned = assignedMRs[laneKey] || [];
			positioned.sort((a, b) => a.position - b.position);
			
			lanes[laneKey] = [
				...positioned.map(item => item.mr),
				...unassignedMRs[laneKey]
			];
		});

		return lanes;
	}, [pendingReviewMRs, myOpenMRs, recentlyClosedMRs, laneAssignments]);

	const allSwimlanes = [
		{ id: "new", title: "New", color: "bg-[var(--color-col-open)]", mrs: categorizeMRs.new },
		{ id: "toReview", title: "To Review", color: "bg-orange-700", mrs: categorizeMRs.toReview },
		{ id: "waitingOnAuthor", title: "Waiting on the Author", color: "bg-amber-600", mrs: categorizeMRs.waitingOnAuthor },
		{ id: "reviewed", title: "Reviewed", color: "bg-green-700", mrs: categorizeMRs.reviewed },
		{ id: "tracking", title: "Tracking", color: "bg-slate-500", mrs: categorizeMRs.tracking },
		{ id: "merged", title: "Merged", color: "bg-[var(--color-col-closed)]", mrs: categorizeMRs.merged },
		{ id: "deleted", title: "Deleted", color: "bg-red-600", mrs: categorizeMRs.deleted },
	];
	
	const swimlanes = allSwimlanes.filter(lane => {
		if (lane.id === "merged" && !showMergedColumn) return false;
		if (lane.id === "deleted" && !showDeletedColumn) return false;
		return true;
	});

	if (loading) {
		return (
			<div className="flex gap-4 pb-4 h-[calc(100vh-140px)]">
				{swimlanes.map((lane) => (
					<div
						key={lane.id}
						className="flex-1 bg-[var(--color-bg-primary)] rounded-lg shadow flex flex-col"
					>
						<div className={`${lane.color} text-white px-4 py-3 rounded-t-lg flex-shrink-0`}>
							<h2 className="font-semibold text-lg">{lane.title}</h2>
							<span className="text-sm opacity-90">...</span>
						</div>
						<div className="p-4 flex-1 overflow-y-auto">
							<div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 mb-3 animate-pulse h-24" />
							<div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 mb-3 animate-pulse h-24" />
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<>
			{error && (
				<div className="bg-[var(--color-error-bg)] border border-[var(--color-error-border)] text-[var(--color-error-text)] px-4 py-3 rounded-lg mb-6 shadow-sm">
					<p className="font-medium">Error loading data</p>
					<p className="text-sm">{error}</p>
				</div>
			)}

			<div className={`flex gap-4 p-4 ${error ? 'h-[calc(100vh-240px)]' : 'h-[calc(100vh-140px)]'}`}>
				{swimlanes.map((lane) => {
				const isHoveringOverLane = hoverPosition && hoverPosition.lane === lane.id;
				
				return (
					<div
						key={lane.id}
						className={`flex-1 bg-[var(--color-bg-primary)] rounded-lg shadow flex flex-col transition-all ${
							isHoveringOverLane ? "ring-2 ring-[var(--color-accent)]" : ""
						}`}
					>
					<div 
						className={`${lane.color} text-white px-4 py-3 rounded-t-lg flex-shrink-0`}
						onDragOver={allowLaneDragAndDrop(lane.id) ? (e) => handleCrossLaneDragOver(e, lane.id) : undefined}
						onDrop={allowLaneDragAndDrop(lane.id) ? (e) => handleCrossLaneDrop(e, lane.id) : undefined}
					>
						<h2 className="font-semibold text-lg">{lane.title}</h2>
						<span className="text-sm opacity-90">{lane.mrs.length} items</span>
					</div>
					<div 
						className="p-4 flex-1 overflow-y-auto"
						onDragOver={allowLaneDragAndDrop(lane.id) ? (e) => handleCrossLaneDragOver(e, lane.id) : undefined}
						onDrop={allowLaneDragAndDrop(lane.id) ? (e) => handleCrossLaneDrop(e, lane.id) : undefined}
					>
						{lane.mrs.length === 0 ? (
							<p className="text-sm text-[var(--color-text-secondary)] text-center py-8">
								No merge requests
							</p>
						) : (
							<>
								{lane.mrs.map((mr, index) => {
									const isBeingDragged = draggedMR && draggedMR.id === mr.id;
									
									return (
										<SwimlaneCard
											key={mr.id}
											mr={mr}
											index={index}
											isDragging={!!draggedMR}
											isBeingDragged={isBeingDragged}
											showIndicator={hoverPosition && hoverPosition.lane === lane.id && hoverPosition.position === index}
											onDragStart={allowLaneDragAndDrop(lane.id) ? () => handleDragStart(mr) : undefined}
											onDragEnd={allowLaneDragAndDrop(lane.id) ? handleDragEnd : undefined}
											onDragOverTop={allowLaneDragAndDrop(lane.id) && !isBeingDragged ? () => {
												if (checkWouldMove(lane.id, index)) {
													setHoverPosition({ lane: lane.id, position: index });
												} else {
													setHoverPosition(null);
												}
											} : undefined}
											onDragOverBottom={allowLaneDragAndDrop(lane.id) && !isBeingDragged ? () => {
												if (checkWouldMove(lane.id, index + 1)) {
													setHoverPosition({ lane: lane.id, position: index + 1 });
												} else {
													setHoverPosition(null);
												}
											} : undefined}
											onDropTop={allowLaneDragAndDrop(lane.id) && !isBeingDragged ? (e) => {
												e.preventDefault();
												e.stopPropagation();
												handleDrop(e, lane.id, index);
											} : undefined}
											onDropBottom={allowLaneDragAndDrop(lane.id) && !isBeingDragged ? (e) => {
												e.preventDefault();
												e.stopPropagation();
												handleDrop(e, lane.id, index + 1);
											} : undefined}
											onOpenDiff={onOpenDiff}
											onDelete={lane.id !== "deleted" ? handleDeleteItem : undefined}
											onRestore={lane.id === "deleted" ? handleRestoreItem : undefined}
											laneId={lane.id}
											enableCommentsExpansion={enableCommentsExpansion}
											searchQuery={searchQuery}
											tags={getMRTags(mr)}
											allTags={allMRTags}
											onAddTag={onAddMRTag}
											onRemoveTag={onRemoveMRTag}
										/>
									);
								})}
								{hoverPosition && hoverPosition.lane === lane.id && hoverPosition.position === lane.mrs.length && (
									<div className="h-1 bg-[var(--color-accent)] rounded-full mx-2 mb-2" />
								)}
							</>
						)}
					</div>
				</div>
					);
				})}
			</div>
		</>
	);
};
